/**
 * FAQ blocks injected via `BLOG_POST_FAQ_MARKER` in post HTML after Decision Framework when present.
 */

export const BLOG_POST_FAQ_MARKER = '<!--blog-mount:blog-faq-->';

export interface IBlogFaqItem {
  /** Stable id for accordion `value` and schema `@id` linkage */
  id: string;
  question: string;
  answer: string;
}

interface IBlogPostFaqConfig {
  /** Slug-specific FAQ list (plain text answers for SEO/schema) */
  items: IBlogFaqItem[];
}

const BEST_TEAM_SCHEDULING_2026_FAQ: IBlogPostFaqConfig = {
  items: [
    {
      id: 'best-team-scheduling-general',
      question: 'What is the best team scheduling software?',
      answer:
        "It depends on what you're optimizing for. If execution visibility across calendars, tasks, and documents matters most — especially for startups and hybrid teams — Tindlo is built for unified timelines. External booking-heavy roles often lean on Calendly. Visual shared calendars frequently point to Teamup. Fully automated scheduling often points to Motion. Use those defaults as anchors, then verify fit with workload and integrations.",
    },
    {
      id: 'is-tindlo-calendar-replacement',
      question: 'Is Tindlo a calendar replacement?',
      answer:
        "Tindlo connects scheduling and execution: it's not strictly 'only a calendar' or 'only tasks.' Teams often sync with Google Calendar and run work on top of timelines with tasks and context tied to time.",
    },
    {
      id: 'best-remote-teams-scheduling',
      question: 'What is the best tool for remote teams?',
      answer:
        'Remote-friendly scheduling usually needs shared visibility beyond meeting slots — what people are committing to execute, when, and together. Collaborative timelines (for example with Tindlo) help hybrid and remote teams coordinate without extra status meetings.',
    },
    {
      id: 'tindlo-vs-calendly',
      question: 'How is Tindlo different from Calendly?',
      answer:
        'Calendly excels at external availability and scheduling links — meetings with clients and inbound booking. Tindlo focuses on team execution: aligning schedules with tasks and deliverables inside a unified timeline workspace.',
    },
    {
      id: 'scheduling-vs-project-management',
      question: 'Can scheduling software replace project management tools?',
      answer:
        'Sometimes partially: strong scheduling solves "when," but project management traditionally covers portfolios, approvals, budgeting, reporting, or complex dependencies beyond calendar placement. Hybrid stacks are common.',
    },
  ],
};

const TINDLO_VS_COMPETITORS_2026_FAQ: IBlogPostFaqConfig = {
  items: [
    {
      id: 'asana-vs-notion',
      question: 'Asana vs Notion: which is better?',
      answer:
        'For execution (ownership, dependencies, reporting), Asana usually wins. For docs and internal knowledge, Notion wins. Many teams run Asana for projects and Notion for docs.',
    },
    {
      id: 'clickup-too-complex',
      question: 'Is ClickUp too complex?',
      answer:
        'Often, yes—especially for small teams that want to ship, not configure. ClickUp is strongest when you commit to setup, templates, and rules early.',
    },
    {
      id: 'best-tool-startups',
      question: 'What is the best tool for startups?',
      answer:
        "If your startup's bottleneck is scheduling and daily execution visibility, Tindlo is a strong default. If you're docs-first, Notion can be the hub.",
    },
    {
      id: 'asana-notion-together',
      question: 'Should startups use Asana or Notion together?',
      answer:
        'Yes—many teams pair them: Asana (or a similar PM tool) for delivery and dependencies, Notion for specs, wikis, and knowledge. The cost is sync overhead, so clarify which system owns "what shipped this week."',
    },
    {
      id: 'tindlo-replace-multiple-tools',
      question: 'Can Tindlo replace multiple tools?',
      answer:
        'Teams often replace a patchwork of calendars, task apps, and doc silos when they want schedules, tasks, and context on one timeline. What you consolidate depends on your workflow—trial a real project end-to-end to see.',
    },
  ],
};

const BY_SLUG: Record<string, IBlogPostFaqConfig> = {
  'tindlo-vs-competitors-2026': TINDLO_VS_COMPETITORS_2026_FAQ,
  'best-team-scheduling-software-2026': BEST_TEAM_SCHEDULING_2026_FAQ,
};

export function getBlogFaqBySlug(slug: string): IBlogFaqItem[] | null {
  return BY_SLUG[slug]?.items ?? null;
}

/** FAQPage structured data for the article URL (paired with UI items). */
export function generateBlogFAQPageSchema(pageUrl: string, items: IBlogFaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
    url: pageUrl,
  };
}
